import React, { Fragment, useState } from "react";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import 'url-search-params-polyfill';
import {
  PageHeader,
  SimpleDataTableWithButtons,
  Section,
  DangerousWrapper,
  makeBreadcrumb,
  slugify,
  findObjIndexByKey,
  toCurrency,
  GatsbyLink,
  TabContainer,
  Tab,
  getObjByValue
} from "@budget-monorepo/shared-ui";

import Layout from "../components/Layout";
import HelmetData from "../components/HelmetData";
import PlanFilter from "../components/PlanFilter";
import "./investment-by-subject.scss";

const InvestmentSubject = props => {
  const { data, location } = props;
  const { content, site, tabsNav } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent.parent;
  const path = location.pathname;
  const pathPrefix = process.env.GATSBY_PATH_PREFIX;
  const relPath = path.replace(pathPrefix, "");
  const { baseSectionURL, budgetSection, title, beneficiaryTable } = content;

  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: baseSectionURL,
    text: budgetSection,
    sections: [title]
  });
  const layoutProps = {
    activeIndex: 1,
    pre: () => <PlanFilter path={relPath} />,
    above: () => <PageHeader title={pageTitle} category={budgetSection} />,
    pageTitle,
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime,
    containerClasses: "main-content main-content--two"
  };

  const tabs = tabsNav.navItems;
  const subTabs = {
    byYearTab: beneficiaryTable.headersBySource.slice(
      1,
      beneficiaryTable.headersBySource.length + 1
    ),
    bySourceTab: beneficiaryTable.headers.slice(
      1,
      beneficiaryTable.headers.length + 1
    )
  };
  
  const urlToTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const tabData = tabs;
      index = findObjIndexByKey({ arr: tabData, value:tabParam, key: tabKey, slug: true });
    }
    return index;
  };

  const urlToSubTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    const subTabParam = params.get('subtab');
    if (tabParam && subTabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const subTabKey = 'name';
      const parentTab = getObjByValue(tabs, tabParam, tabKey);
      const subTabData = subTabs[parentTab.dataKey];
      index = findObjIndexByKey({ arr: subTabData, value: subTabParam, key: subTabKey, slug: true});
    }
    return index;
  };

  const [tabIndex, setTabIndex] = useState(urlToTabIndex(location));
  const [subTabIndex, setSubTabIndex] = useState(urlToSubTabIndex(location));

  const isDataByYear = tabIndex === 0;

  const getSubComponent = (row, columns) => (
    <Fragment>
      {row.original.planItems.map((item, index) => {
        const dataSetKey = isDataByYear ? "data" : "dataBySource";
        const subTabKey = isDataByYear ? "bySourceTab" : "byYearTab"; // use the reversed subTab data to get the column header for each line item row
        const planItemLink = `/beneficiary-agency/${slugify(
          item.govLevelOne
        )}/${slugify(item.govLevelTwo)}/${slugify(item.planItemNumber)}`;

        return (
          // Suppress plan item with "suppressed" flag
          // eslint-disable-next-line react/no-array-index-key
          <div
            className={`rt-tr ${item.suppressed ? "suppressed" : ""}`}
            role="row"
            key={`planitem-${item.planItemNumber}-${index}`}
          >
            <div
              className="rt-td rt-expandable"
              style={{ flex: "35 0 auto", width: "35px", maxWidth: "35px" }}
            />
            <div
              className={`rt-td ${columns[0].className}`}
              style={{
                flex: `${columns[0].minWidth} 0 auto`,
                width: `${columns[0].minWidth}px`
              }}
            >
              <GatsbyLink to={planItemLink}>{item.planItemNumber}</GatsbyLink>
              <DangerousWrapper
                rawHtml={item.planItemName}
                htmlTag="span"
                className="plan-item-name"
              />
            </div>
            {subTabs[subTabKey].map((record, i) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <DangerousWrapper
                  key={`wrapper-${columns[i + 1].className}-${index}-${i}`}
                  rawHtml={toCurrency(
                    item[dataSetKey][subTabIndex][record.value]
                  )}
                  htmlTag="div"
                  className={`rt-td ${columns[i + 1].className}`}
                  style={{
                    flex: `${columns[i + 1].minWidth} 0 auto`,
                    width: `${columns[i + 1].minWidth}px`
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </Fragment>
  );

  const table = (key, tab) => {
    const td = getObjByValue(beneficiaryTable[key], tab.value, "value");
    return {
      data: td && td.data,
      total: td && td.total,
      headers: isDataByYear
        ? beneficiaryTable.headers
        : beneficiaryTable.headersBySource
    };
  };

  // persist the last selected level
  const defaultExpLevel = (global.window && parseInt(window.sessionStorage.getItem('level')) )|| 0;

  const simpleDataTableWithButtonsProps = (subTabKey, subTab) => ({
    onClickCallback: (e) => {
      if (global.window) {
        window.sessionStorage.setItem('level', e.level);
      }
    },
    defaultExpLevel,
    simpleDataTableProps: {
      table: table(subTabKey, subTab),
      maxColWidth: 300,
      download: `${location.pathname}/${subTab.name}`,
      title: `${budgetSection} ${title}`,
      getSubComponent,
      renderSubComponent: true
    },
    treeTableButtonsProps: {
      a11yTitle: title,
      headerLevel: 2,
      buttons: [
        {
          info:
            "View the capital agency's plan items grouped by beneficiary agency.",
          text: "View by beneficiary agency",
          accessor: "govLevelTwo",
          expLevel: 0
        },
        {
          info: "View all of the plan items under this capital agency.",
          text: "View by plan item",
          accessor: "planItems",
          expLevel: 1
        }
      ]
    }
  });

  const onTabChange = (tabRef, curLocation) => {
    const { location: newLocation } = props;
    if (global.window && curLocation.href) {
      const tab = tabRef.current.innerText;
      const { pathname } = newLocation;
      const url = `${pathname}?tab=${slugify(tab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: tabs, value: tab, key: 'text'});
      setTabIndex(index);
      // reset sub tab on tab change
      setSubTabIndex(0);
    }
  };


  const onSubTabChange = ({tabRef, location: curLocation, parentTab}) => {
    const { location } = props;
    if (global.window && curLocation.href && tabRef.current) {
      const subTab = tabRef.current.innerText;
      const { pathname } = location;
      const url = `${pathname}?tab=${parentTab.href}&subtab=${slugify(subTab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: subTabs[parentTab.dataKey], value: subTab, key: 'name'});
      setSubTabIndex(index);
    }
  };

  // metadata for the page
  const metaTitle = `${budgetSection}: ${title}`;
  const metaDescription = `Capital investment ${metaTitle.toLowerCase()}.`;


  return (
    <Fragment>
      <HelmetData
        {...{
          metaTitle,
          metaDescription,
          metaType: "dataset",
          mg_mod_date,
          mg_mod_datetime
        }}
      />
      <Layout {...layoutProps}>
        {beneficiaryTable && (
          <Section>
            {tabs && (
              <TabContainer
                defaultTab={tabIndex}
                onTabChange={tabRef => onTabChange(tabRef, location)}
              >
                {tabs.map(tab => (
                  <Tab
                    handleClick={function noRefCheck() {}}
                    title={tab.text}
                    key={`tab-${tab.href}`}
                  >
                    <TabContainer
                      nested
                      defaultTab={subTabIndex}
                      onTabChange={tabRef =>
                        onSubTabChange({ tabRef, location, parentTab: tab })
                      }
                    >
                      {subTabs[tab.dataKey].map((subTab, i) => {
                        return (
                          <Tab
                            title={subTab.name}
                            key={`subtab-${subTab.value}`}
                          >
                            <SimpleDataTableWithButtons
                              {...simpleDataTableWithButtonsProps(tab.dataKey, subTab)}
                            />
                          </Tab>
                        );
                      })}
                    </TabContainer>
                  </Tab>
                ))}
              </TabContainer>
            )}
          </Section>
        )}
      </Layout>
    </Fragment>
  );
};

InvestmentSubject.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default InvestmentSubject;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    tabsNav: navJson(navType: { eq: "tabs" }) {
      ...SiteNav
    }
    content: planInvestmentSubject(fields: { slug: { eq: $slug } }) {
      budgetSection
      title
      baseSectionURL
      beneficiaryTable {
        headers {
          name
          type
          align
          value
        }
        headersBySource {
          name
          type
          align
          value
        }
        byYearTab {
          name
          value
          data: d {
            govLevelTwo
            govLevelOne
            govLevelOneDisplay
            govLevelTwoDisplay
            stateBondCap
            nonBondCap
            operatingFunds
            federalFunds
            otherFunds
            total
            planItems {
              suppressed
              planItemName
              planItemNumber
              govLevelOne
              govLevelTwo
              govLevelTwoDisplay
              data {
                stateBondCap
                nonBondCap
                operatingFunds
                federalFunds
                otherFunds
                total
              }
            }
          }
          total {
            govLevelTwo
            year
            stateBondCap
            nonBondCap
            operatingFunds
            federalFunds
            otherFunds
            total
          }
        }
        bySourceTab {
          name
          value
          data: d {
            govLevelOne
            govLevelOneDisplay
            govLevelTwo
            govLevelTwoDisplay
            fy1
            fy2
            fy3
            fy4
            fy5
            fy6
            planItems {
              suppressed
              planItemName
              planItemNumber
              govLevelOne
              govLevelTwo
              govLevelTwoDisplay
              dataBySource {
                fy1
                fy2
                fy3
                fy4
                fy5
                fy6
                source
                value
              }
            }
          }
          total {
            fy1
            fy2
            fy3
            fy4
            fy5
            fy6
            govLevelTwo
            source
            value
          }
        }
      }
      # Get the last modified timestamp for the file powering the plan item pages
      ...InvestmentSubjectDataTimeStamp
    }
  }
`;
